import {get} from 'lodash-es';
import React, {
    Suspense, useEffect, useState, FC,
} from 'react';
import {useDispatch} from 'react-redux';
import {
    Redirect, Route, Switch, useLocation,
} from 'react-router-dom';

import NewSideMenu from 'components/@common/specific/new-side-menu';
import {AIS_ROUTES} from 'components/ais/ais.constants';
import {selectEntityMetadata, selectMetadata, selectTableEntityMetadata} from 'entities/metadata';
import {setEntityNamesFetchedWithContext, updateContextRawData} from 'modules/context/context-actions';
import {fetchRequestListInfo} from 'modules/data/api/documents-upload-monitoring-api';
import {
    createActionHandlerForTableChange,
    resetAllDataWithException,
    setNotificationDuration,
} from 'modules/data/data-actions';
import {selectNotificationDuration} from 'modules/data/data-selectors';
import {getPageTemplate} from 'pages';
import {AisAnswerFormationPage} from 'pages/ais/comments-request-result';
import {AIS3_ORG_STRUCTURE_ENTITY_NAME} from 'pages/ais/organization-structure/organization-structure-page.constants';
import {ETL_AUDIT_ENTITY_NAME, EtlAuditPage} from 'pages/external-loading/etl-audit-page';
import {ForbiddenForm} from 'pages/forbidden-page/forbidden-form';
import {PDFLinkFallbackPage} from 'pages/pdf-link-fallback-page';
import {SynthesisDocumentPage} from 'pages/synthesis-document/synthesis-document-page';
import {URL_SYNTHESIS_DOCUMENT} from 'pages/synthesis-document/synthesis-document.constants';
import {TableReportPage} from 'pages/table-report-page';
import {WidgetPage} from 'pages/widget-page';
import {EEntityType} from 'shared/constants/entities';
import {
    URL_AUTH_ROUTER,
    URL_ETL_AUDIT,
    URL_WIDGETS,
} from 'shared/constants/urls';
import {LocationService} from 'shared/hooks/use-entity-from-location/use-entity-from-location.service';
import {showMessageFromResponse} from 'shared/utils';
import {useGetAppPropertyQuery} from 'store/api/app-properties/app-properties.api';
import {useAppSelector} from 'store/config/hooks';
import {
    loadUserInfo,
    selectAuthError,
    selectIsAuthenticated,
    loadMicroserviceToken,
} from 'store/slices/auth-slice';
import {
    selectMenuStructure, selectMenuStructureIdMap, generateRoute, isItemTypeTabsWithReferences,
} from 'store/slices/menu-slice';
import {loadMenuStructure} from 'store/slices/menu-slice/menu-slice-thunks';
import {TMenuStructure, IMenuStructureItem} from 'store/slices/menu-slice/menu-slice-types';
import {tableReportSliceActions} from 'store/slices/table-report-slice';
import {useWebsocket} from 'websockets';
import {WebsocketsContext} from 'websockets/core';

import {Breadcrumbs} from '../breadcrumbs';
import {parseBreadcrumbsFromMenuItem} from '../breadcrumbs/utils/breadcrumb-generator';
import {ContextFilter} from '../context-filter';
import {DraggableFrameRoot} from '../draggable-frame';
import {Footer} from '../footer';
import {Header} from '../header';
import {ModalRoot} from '../modal';
import {Spinner} from '../spinner';
import {
    DownloadReportDocumentsModal,
} from '../table-report/components/table-report-modal/download-report-documents-modal';
import {
    defineEntityName,
    getEntityType,
    getMenuNavLinks,
    splitUrl,
    TABLE_REPORT_ENTITY_NAME,
} from './layout-utils';

import './layout.less';

interface ILayoutProps {
    currentEntityName: string;
    contextFilterData?: Record<string, any>;
    loadStructure: () => void;
    changeData: (
        entityName: string,
        entityType: EEntityType,
        data: Record<string, any>,
    ) => void;
    menuStructure: TMenuStructure | null;
    menuStructureIdMap: Record<string, IMenuStructureItem> | null;
    isAuthorized: boolean;
    match?: {
        params: { entityName: string };
    };
}

// const SideMenu = React.lazy(() => import('../@common/specific/new-side-menu'));

const LayoutComponent: FC<ILayoutProps> = ({
    match,
}: ILayoutProps) => {
    const dispatch = useDispatch();
    const currentLocationUrl = useLocation<History>().pathname;
    const currentEntityName = get(match, 'params.entityName', '');
    const tabEntityName = LocationService.getTabEntityNameFromLocation(currentLocationUrl);
    const getCurrentEntityMetaSelector = selectEntityMetadata(currentEntityName);
    const currentEntityMeta = useAppSelector(state => getCurrentEntityMetaSelector(state));
    const currentEntityType = currentEntityMeta && Object.keys(currentEntityMeta)[0] as EEntityType;
    const currentLocation = splitUrl(currentLocationUrl, '/', currentEntityName);

    const menuStructure = useAppSelector(selectMenuStructure);
    const menuStructureIdMap = useAppSelector(selectMenuStructureIdMap);
    const isAuthorized = useAppSelector(selectIsAuthenticated);

    const menuEntityType: EEntityType | false | null = menuStructure && !currentEntityType
        && menuStructure?.find(menu => menu.entityName === currentEntityName)?.entityType;

    const isTabWithReferences = tabEntityName &&
        menuEntityType &&
        currentEntityName !== tabEntityName &&
        menuEntityType === EEntityType.TABS_WITH_REFERENCES;

    const tabEntityType = isTabWithReferences
        ? menuStructure?.find(menu => menu.entityName === tabEntityName)?.entityType
        : null;

    const metadataType = isTabWithReferences && tabEntityType ? tabEntityType : currentEntityType;

    const locationOrEntityName = defineEntityName(
        isTabWithReferences ? tabEntityName : currentEntityName,
        currentLocation,
        currentLocationUrl,
    );
    const isPaginationDisabled = useAppSelector(selectTableEntityMetadata(locationOrEntityName))?.disabledPagination;
    const metadata = useAppSelector(selectMetadata(locationOrEntityName, metadataType));
    const isContextDisabled = menuEntityType as unknown as EEntityType === EEntityType.TABLE_REPORT
        || locationOrEntityName === TABLE_REPORT_ENTITY_NAME
        || locationOrEntityName === AIS3_ORG_STRUCTURE_ENTITY_NAME;
    const {setReportDownloadsData} = tableReportSliceActions;
    const menuNavLinks = getMenuNavLinks(menuStructure);
    const selectedNotificationData = useAppSelector(selectNotificationDuration());
    const {data: notificationData} = useGetAppPropertyQuery(
        {propertyCode: 'TIMEOUT_NOTY_SETTING'},
        {skip: false},
    );
    const isForbidden = useAppSelector(selectAuthError)?.code === 403;

    const loadStructure = () => dispatch(loadMenuStructure());
    const loadUser = () => dispatch(loadUserInfo());
    const loadExternalToken = () => dispatch(loadMicroserviceToken());

    const [collapsed, setCollapsed] = useState(true);

    useEffect(() => {
        if (isAuthorized) {
            loadUser();
            loadExternalToken();
        }
    }, [isAuthorized]);

    useEffect(() => {
        if (!menuStructure) {
            loadStructure();
            fetchRequestListInfo().then(result => {
                dispatch(setReportDownloadsData(result.data));
            }).catch(er => showMessageFromResponse({response: er?.response, isError: true}));
        }
    }, [menuStructure]);

    useEffect(() => {
        if (!selectedNotificationData && notificationData && notificationData.TIMEOUT_NOTY_SETTING) {
            dispatch(setNotificationDuration({notificationDuration: notificationData.TIMEOUT_NOTY_SETTING}));
        }
    }, [selectedNotificationData]);

    const {wsInstance} = useWebsocket();

    if (!isAuthorized) {
        return <Redirect to={URL_AUTH_ROUTER} />;
    }

    return (
        <Suspense fallback={(
            <div className="content-layout">
                <div className="layout__spinner">
                    <Spinner tip="Подождите..." />
                </div>
            </div>
        )}
        >
            <WebsocketsContext.Provider value={{
                wsInstance,
            }}
            >
                <div className="layout-wrapper">
                    <div className="layout-side-menu">
                        <NewSideMenu />
                    </div>
                    <div className="layout">
                        <Header
                            menuContent={<DownloadReportDocumentsModal />}
                            menuNavLinks={menuNavLinks}
                        >
                            <ContextFilter
                                onChange={values => {
                                    const entityName = isTabWithReferences ? tabEntityName : currentEntityName;
                                    const entityType = (isTabWithReferences && tabEntityType) ? tabEntityType
                                        : (currentEntityType || menuEntityType);

                                    dispatch(setEntityNamesFetchedWithContext([]));
                                    dispatch(updateContextRawData({rawData: values}));
                                    dispatch(resetAllDataWithException({
                                        entityName,
                                        entityType,
                                    }));

                                    dispatch(createActionHandlerForTableChange(EEntityType.FILTER)({
                                        entityName,
                                        newEntityTypeData: values,
                                        url: currentLocationUrl,
                                        parentEntityType: entityType,
                                        prefix: metadata?.prefix,
                                        disabledPagination: metadata?.disabledPagination || isPaginationDisabled,
                                        urlConfig: metadata?.urlConfig,
                                    }));
                                }}
                                isDisabled={isContextDisabled}
                            />
                        </Header>
                        <section className="layout__content">
                            {!collapsed && (
                                <div
                                    className="layout__content darken"
                                    onClick={() => setCollapsed(true)}
                                />
                            )}
                            {isForbidden
                                ? <ForbiddenForm lastLocation={currentLocationUrl} />
                                : (
                                    <Switch>
                                        <Route
                                            path={URL_SYNTHESIS_DOCUMENT}
                                            exact
                                            render={() => (
                                                <>
                                                    <Breadcrumbs
                                                        breadcrumbs={parseBreadcrumbsFromMenuItem(
                                                            menuStructureIdMap,
                                                            'Настройка синтеза документов',
                                                            'administration.settings.synthesis.documents' as any,
                                                            'administration.settings.synthesis.documents',
                                                            null as any,
                                                            currentLocationUrl,
                                                        )}
                                                    />
                                                    <SynthesisDocumentPage />
                                                </>
                                            )}
                                        />
                                        <Route
                                            path="/pdf-link-fallback"
                                            exact
                                            component={PDFLinkFallbackPage}
                                        />
                                        <Route
                                            path={URL_WIDGETS}
                                            exact
                                            component={WidgetPage}
                                        />
                                        <Route
                                            path="/table-reports/:id?/:templateCode"
                                            exact
                                            render={props => {
                                                const {id, templateCode} = props?.match.params || {};
                                                const templateCodeEntityName = menuStructure
                                                    ?.find(m => m.templateCode === templateCode)?.entityName;
                                                return (
                                                    <TableReportPage
                                                        entityName={templateCodeEntityName}
                                                        docId={id ?? null}
                                                        templateCode={templateCode}
                                                    />
                                                );
                                            }}
                                        />
                                        <Route
                                            path={`/${AIS_ROUTES.COMMENTS_REQUEST_RESULT}/comments-request/:id`}
                                            exact
                                            render={props => {
                                                const {id} = props?.match.params || {};
                                                return (
                                                    <AisAnswerFormationPage
                                                        requestId={id as unknown as number}
                                                        entityName={AIS_ROUTES.COMMENTS_REQUEST_RESULT}
                                                    />
                                                );
                                            }}
                                        />
                                        <Route
                                            path={URL_ETL_AUDIT}
                                            exact
                                            render={() => (
                                                <>
                                                    <Breadcrumbs
                                                        breadcrumbs={parseBreadcrumbsFromMenuItem(
                                                            menuStructureIdMap,
                                                            'Аудит ETL-процессов',
                                                            undefined,
                                                            ETL_AUDIT_ENTITY_NAME,
                                                            undefined,
                                                            currentLocationUrl,
                                                        )}
                                                    />
                                                    <EtlAuditPage />
                                                </>
                                            )}
                                        />
                                        {menuStructure?.map(menuItem => {
                                            let actualItem = menuItem;
                                            const isEmptyTab = !menuItem.children?.length
                                                && isItemTypeTabsWithReferences(menuItem?.entityType);
                                            if (menuItem.children?.length) {
                                                [actualItem] = menuItem.children;
                                            }

                                            const actualItemType = isEmptyTab
                                                ? EEntityType.TABLE
                                                : actualItem?.entityType;

                                            const parentItem = menuStructureIdMap
                                                ? menuStructureIdMap[actualItem.parentId as string]
                                                : undefined;

                                            const useParentItem = isItemTypeTabsWithReferences(parentItem?.entityType)
                                                || isItemTypeTabsWithReferences(actualItem?.entityType);

                                            const entityType = getEntityType({
                                                isEmptyTab,
                                                useParentItem,
                                                parentType: parentItem?.entityType,
                                                actualItemType: actualItem?.entityType,
                                            });

                                            const parentEntityName = (useParentItem && !isEmptyTab)
                                                ? parentItem?.entityName : '';

                                            const url = generateRoute({
                                                ...actualItem,
                                                parentEntityName,
                                                entityType: actualItemType,
                                            });

                                            if (!url.length) {
                                                return null;
                                            }

                                            return (
                                                <Route
                                                    key={url}
                                                    path={url}
                                                    render={props => {
                                                        const TemplateComponent = getPageTemplate(entityType as string);
                                                        const breadcrumbs = parseBreadcrumbsFromMenuItem(
                                                            menuStructureIdMap,
                                                            actualItem.itemTitle,
                                                            actualItemType,
                                                            actualItem.entityName,
                                                            parentItem,
                                                            currentLocationUrl,
                                                        );
                                                        return (
                                                            <>
                                                                <Breadcrumbs
                                                                    breadcrumbs={breadcrumbs}
                                                                />
                                                                <TemplateComponent
                                                                    {...props}
                                                                    url={url}
                                                                    entityName={actualItem.entityName}
                                                                    entityType={actualItem.entityType}
                                                                    parentItem={isEmptyTab ? actualItem : parentItem}
                                                                    actualItem={actualItem}
                                                                />
                                                            </>
                                                        );
                                                    }}
                                                />
                                            );
                                        })}
                                        {menuStructure?.map(menuItem => {
                                            const isTabsWithReferencesParent = isItemTypeTabsWithReferences(
                                                menuItem?.entityType,
                                            ) && Boolean(menuItem?.children?.length);

                                            if (!isTabsWithReferencesParent || !menuItem?.entityName) {
                                                return null;
                                            }

                                            const url = `/${menuItem.entityName}`;

                                            return (
                                                <Route
                                                    key={url}
                                                    path={url}
                                                    render={props => {
                                                        const TemplateComponent = getPageTemplate(
                                                            menuItem.entityType as string,
                                                        );
                                                        const breadcrumbs = parseBreadcrumbsFromMenuItem(
                                                            menuStructureIdMap,
                                                            menuItem?.itemTitle,
                                                            menuItem?.entityType,
                                                            menuItem?.entityName,
                                                            currentLocationUrl,
                                                        );

                                                        return (
                                                            <>
                                                                <Breadcrumbs
                                                                    breadcrumbs={breadcrumbs}
                                                                />
                                                                <TemplateComponent
                                                                    {...props}
                                                                    url={url}
                                                                    entityName={menuItem?.entityName}
                                                                    entityType={menuItem?.entityType}
                                                                    actualItem={menuItem}
                                                                />
                                                            </>
                                                        );
                                                    }}
                                                />
                                            );
                                        })}
                                    </Switch>
                                )}
                        </section>
                        <Footer />
                        <ModalRoot />
                        <DraggableFrameRoot />
                    </div>
                </div>
            </WebsocketsContext.Provider>
        </Suspense>
    );
};

export const Layout = LayoutComponent;
